import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  FiPlus,
  FiSmile,
  FiAtSign,
  FiPaperclip,
  FiSend,
} from "react-icons/fi";
import { uploadFiles } from "../../../APIServices/GoApiServices/chatService";
import TextEditor from "./TextEditor";
import { IoMdSend } from "react-icons/io";

const EMOJIS = ["😀", "😂", "😍", "👍", "🔥", "🎉", "😎", "🤔", "🙌", "🚀"];

const ChatComposer = ({ channelName, onSend, onTypingStart, onTypingStop, chatToken }) => {
  const [message, setMessage] = useState("");
  const [showEmoji, setShowEmoji] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [pendingFiles, setPendingFiles] = useState([]);

  const textareaRef = useRef(null);
  const fileRef = useRef(null);
  const typingRef = useRef({ isTyping: false, stopTimer: null });

  const startTyping = useCallback(() => {
    if (typingRef.current.isTyping) return;
    typingRef.current.isTyping = true;
    onTypingStart?.();
  }, [onTypingStart]);

  const scheduleStopTyping = useCallback(() => {
    clearTimeout(typingRef.current.stopTimer);
    typingRef.current.stopTimer = setTimeout(() => {
      if (!typingRef.current.isTyping) return;
      typingRef.current.isTyping = false;
      onTypingStop?.();
    }, 1200);
  }, [onTypingStop]);

  useEffect(() => {
    return () => clearTimeout(typingRef.current.stopTimer);
  }, []);

  const clearPending = useCallback(() => {
    setPendingFiles([]);
    if (fileRef.current) fileRef.current.value = "";
  }, []);

  const sendMessage = useCallback(async () => {
    const hasText = !!message.trim();
    const hasFiles = pendingFiles.length > 0;

    if (!hasText && !hasFiles) return;
    if (!chatToken) {
      alert("Missing chat token");
      return;
    }
    if (uploading) return;

    if (typingRef.current.isTyping) {
      typingRef.current.isTyping = false;
      onTypingStop?.();
    }

    try {
      let metas = [];

      if (hasFiles) {
        setUploading(true);
        metas = await uploadFiles(pendingFiles, chatToken);
      }
      onSend?.({ text: message, files: metas });
      setMessage("")
      clearPending();
      setShowEmoji(false);
    } catch {
      alert(hasFiles ? "Files upload failed" : "Message send failed");
    } finally {
      setUploading(false);
    }
  }, [message, pendingFiles, chatToken, uploading, onSend, onTypingStop, clearPending]);

  const handleChange = useCallback(
    (val) => {
      setMessage(val);

      if (val.trim()) {
        startTyping();
        scheduleStopTyping();
        return;
      }

      clearTimeout(typingRef.current.stopTimer);
      if (typingRef.current.isTyping) {
        typingRef.current.isTyping = false;
        onTypingStop?.();
      }
    },
    [startTyping, scheduleStopTyping, onTypingStop]
  );

  const addEmoji = useCallback(
    (emoji) => {
      const el = textareaRef.current;
      const cursorPos = el?.selectionStart ?? message.length;
      const next = message.slice(0, cursorPos) + emoji + message.slice(cursorPos);
      setMessage(next);
      setShowEmoji(false);
      setTimeout(() => textareaRef.current?.focus(), 0);
    },
    [message]
  );

  const openPicker = useCallback(() => {
    if (!chatToken || uploading) return;
    fileRef.current?.click();
  }, [chatToken, uploading]);

  const onPickFiles = useCallback((e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (files.length === 0) return;

    setPendingFiles((prev) => [...prev, ...files]);
  }, []);

  const removePendingAt = useCallback((index) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  return (
    <div className="chat-composer">
      <TextEditor
        placeholder={"Write Message..."}
        value={message}
        onChange={(val) => {
          setMessage(val);
          handleChange(val)
        }}
        onSubmit={sendMessage}
      />
      <input
        ref={fileRef}
        type="file"
        multiple
        style={{ display: "none" }}
        onChange={onPickFiles}
      />

      <div className="composer-actions ps-2">
        <div className="left">
          <FiPlus />
          <span className="text-icon">Aa</span>

          <div className="emoji-wrapper">
            <FiSmile onClick={() => setShowEmoji((v) => !v)} />
            {showEmoji && (
              <div className="emoji-picker">
                {EMOJIS.map((emoji) => (
                  <span key={emoji} onClick={() => addEmoji(emoji)}>
                    {emoji}
                  </span>
                ))}
              </div>
            )}
          </div>

          <FiAtSign />
          <FiPaperclip
            onClick={openPicker}
            style={{ cursor: "pointer", opacity: uploading ? 0.5 : 1 }}
            title={uploading ? "Uploading..." : "Attach files"}
          />
        </div>

        <button
          className="send-btn rounded-0"
          onClick={sendMessage}
          disabled={uploading}
          style={uploading ? { opacity: 0.6, cursor: "not-allowed" } : undefined}
          title={uploading ? "Uploading..." : "Send"}
        >
          <IoMdSend />
        </button>
      </div>

      {pendingFiles.length > 0 && (
        <div style={{ padding: "6px 10px", fontSize: 12, opacity: 0.85 }}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {pendingFiles.map((f, idx) => (
              <div
                key={`${f.name}-${idx}`}
                style={{
                  padding: "4px 8px",
                  border: "1px solid rgba(0,0,0,0.1)",
                  borderRadius: 6,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                }}
                title={f.name}
              >
                <span style={{ maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {f.name}
                </span>
                <span
                  onClick={() => removePendingAt(idx)}
                  style={{ cursor: "pointer", fontWeight: 700 }}
                  title="Remove"
                >
                  ×
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {uploading && (
        <div style={{ fontSize: 12, opacity: 0.75, padding: "6px 10px" }}>
          Uploading files...
        </div>
      )}
    </div>
  );
};

export default ChatComposer;
